import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  DatePicker,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Spin,
  message,
  Divider,
  Switch
} from 'antd';
import {
  DownloadOutlined,
  PrinterOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import apiService from '@/services/apiService';

const { Title, Text } = Typography;

interface BalanceSheetLineItem {
  accountId: string;
  accountCode: string;
  accountName: string;
  balance: number;
  previousBalance?: number;
  percentOfTotal?: number;
}

interface BalanceSheetData {
  asOfDate: string;
  previousDate?: string;
  assets: {
    current: BalanceSheetLineItem[];
    nonCurrent: BalanceSheetLineItem[];
    totalCurrent: number;
    totalNonCurrent: number;
    total: number;
    previousTotal?: number;
  };
  liabilities: {
    current: BalanceSheetLineItem[];
    nonCurrent: BalanceSheetLineItem[];
    totalCurrent: number;
    totalNonCurrent: number;
    total: number;
    previousTotal?: number;
  };
  equity: {
    items: BalanceSheetLineItem[];
    retainedEarnings: number;
    total: number;
    previousTotal?: number;
  };
  totalLiabilitiesAndEquity: number;
  balanced: boolean;
}

const formatCurrency = (amount: number) => {
  if (amount === 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'HKD',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatPercent = (value: number | undefined) => {
  if (value === undefined || value === 0) return '-';
  return `${value.toFixed(1)}%`;
};

const BalanceSheetReport: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<BalanceSheetData | null>(null);
  const [asOfDate, setAsOfDate] = useState<Dayjs>(dayjs());
  const [comparePreviousDate, setComparePreviousDate] = useState<Dayjs | null>(null);
  const [showPercentages, setShowPercentages] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        asOfDate: asOfDate.format('YYYY-MM-DD'),
        showPercentages: showPercentages.toString()
      });

      if (comparePreviousDate) {
        params.append('comparePreviousDate', comparePreviousDate.format('YYYY-MM-DD'));
      }

      const response = await apiService.get(`/accounting/reports/balance-sheet?${params}`);
      setData(response.data || response);
    } catch (error: any) {
      message.error(error.message || 'Failed to load balance sheet');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /**
   * Open the General Ledger on the entries behind one balance.
   *
   * Deliberately no start date. A balance sheet figure is the total of every
   * posting up to the as-of date, so the ledger has to show all of them for
   * the list to add up to the balance. Restricting it to, say, this year would
   * leave out the opening position and the two would not agree.
   */
  const openLedgerFor = (accountId: string) => {
    const params = new URLSearchParams({
      accountId,
      toDate: asOfDate.format('YYYY-MM-DD'),
    });
    navigate(`/accounting/general-ledger?${params.toString()}`);
  };

  const baseColumns: ColumnsType<BalanceSheetLineItem> = [
    {
      title: 'Account Code',
      dataIndex: 'accountCode',
      key: 'accountCode',
      width: 120,
      render: (code: string, row) => (
        <a onClick={() => openLedgerFor(row.accountId)}>{code}</a>
      )
    },
    {
      title: 'Account Name',
      dataIndex: 'accountName',
      key: 'accountName',
      width: 250,
      render: (name: string, row) => (
        <a onClick={() => openLedgerFor(row.accountId)}>{name}</a>
      )
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      width: 150,
      align: 'right',
      render: (val: number, row) => (
        <a onClick={() => openLedgerFor(row.accountId)}>
          <strong>{formatCurrency(val)}</strong>
        </a>
      )
    }
  ];

  const extraColumns: ColumnsType<BalanceSheetLineItem> = [];

  if (comparePreviousDate) {
    extraColumns.push({
      title: 'Previous Balance',
      dataIndex: 'previousBalance',
      key: 'previousBalance',
      width: 150,
      align: 'right',
      render: (val: number) => formatCurrency(val || 0)
    });
  }

  if (showPercentages) {
    extraColumns.push({
      title: '% of Total',
      dataIndex: 'percentOfTotal',
      key: 'percentOfTotal',
      width: 100,
      align: 'right',
      render: (val: number) => formatPercent(val)
    });
  }

  const columns = [...baseColumns, ...extraColumns];

  const handleExport = () => {
    if (!data) {
      message.warning('No data to export');
      return;
    }
    const rows: Record<string, any>[] = [];
    const addSection = (title: string, items: BalanceSheetLineItem[], subtotal: number, subtotalLabel: string) => {
      rows.push({ 'Section': title, 'Account Code': '', 'Account Name': '', 'Balance': '' });
      items.forEach(item => {
        rows.push({ 'Section': '', 'Account Code': item.accountCode, 'Account Name': item.accountName, 'Balance': Number(item.balance || 0) });
      });
      rows.push({ 'Section': '', 'Account Code': '', 'Account Name': subtotalLabel, 'Balance': Number(subtotal || 0) });
      rows.push({ 'Section': '', 'Account Code': '', 'Account Name': '', 'Balance': '' });
    };
    addSection('ASSETS - Current', data.assets.current, data.assets.totalCurrent, 'Total Current Assets');
    addSection('ASSETS - Non-Current', data.assets.nonCurrent, data.assets.totalNonCurrent, 'Total Non-Current Assets');
    rows.push({ 'Section': '', 'Account Code': '', 'Account Name': 'TOTAL ASSETS', 'Balance': Number(data.assets.total || 0) });
    rows.push({ 'Section': '', 'Account Code': '', 'Account Name': '', 'Balance': '' });
    addSection('LIABILITIES - Current', data.liabilities.current, data.liabilities.totalCurrent, 'Total Current Liabilities');
    addSection('LIABILITIES - Non-Current', data.liabilities.nonCurrent, data.liabilities.totalNonCurrent, 'Total Non-Current Liabilities');
    rows.push({ 'Section': '', 'Account Code': '', 'Account Name': 'TOTAL LIABILITIES', 'Balance': Number(data.liabilities.total || 0) });
    rows.push({ 'Section': '', 'Account Code': '', 'Account Name': '', 'Balance': '' });
    rows.push({ 'Section': 'EQUITY', 'Account Code': '', 'Account Name': '', 'Balance': '' });
    data.equity.items.forEach(item => {
      rows.push({ 'Section': '', 'Account Code': item.accountCode, 'Account Name': item.accountName, 'Balance': Number(item.balance || 0) });
    });
    rows.push({ 'Section': '', 'Account Code': '', 'Account Name': 'TOTAL EQUITY', 'Balance': Number(data.equity.total || 0) });
    rows.push({ 'Section': '', 'Account Code': '', 'Account Name': '', 'Balance': '' });
    rows.push({ 'Section': '', 'Account Code': '', 'Account Name': 'TOTAL LIABILITIES & EQUITY', 'Balance': Number(data.totalLiabilitiesAndEquity || 0) });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 22 }, { wch: 14 }, { wch: 30 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Balance Sheet');
    XLSX.writeFile(wb, `Balance_Sheet_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
    message.success('Balance Sheet exported');
  };

  const handlePrint = () => {
    window.print();
  };

  const renderSectionTable = (
    title: string,
    items: BalanceSheetLineItem[],
    subtotal: number,
    subtotalLabel: string,
    bgColor: string
  ) => (
    <>
      <Text strong style={{ fontSize: 14, marginBottom: 8, display: 'block' }}>{title}</Text>
      <Table
        columns={columns}
        dataSource={items}
        rowKey="accountId"
        pagination={false}
        size="small"
        summary={() => (
          <Table.Summary>
            <Table.Summary.Row style={{ backgroundColor: bgColor }}>
              <Table.Summary.Cell index={0} colSpan={2}>
                <Text strong>{subtotalLabel}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                <Text strong>{formatCurrency(subtotal)}</Text>
              </Table.Summary.Cell>
              {comparePreviousDate && (
                <Table.Summary.Cell index={3} align="right">-</Table.Summary.Cell>
              )}
              {showPercentages && (
                <Table.Summary.Cell index={4} align="right">-</Table.Summary.Cell>
              )}
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </>
  );

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>Balance Sheet</Title>
          {data && (
            data.balanced ? (
              <Tag icon={<CheckCircleOutlined />} color="success">Balanced</Tag>
            ) : (
              <Tag icon={<WarningOutlined />} color="error">Out of Balance</Tag>
            )
          )}
        </Space>
      }
      extra={
        <Space>
          <Space>
            <Text>Show %:</Text>
            <Switch checked={showPercentages} onChange={setShowPercentages} />
          </Space>
          <DatePicker
            value={asOfDate}
            onChange={(date) => date && setAsOfDate(date)}
            format="YYYY-MM-DD"
            placeholder="As of Date"
          />
          <DatePicker
            value={comparePreviousDate}
            onChange={setComparePreviousDate}
            format="YYYY-MM-DD"
            placeholder="Compare Date"
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={fetchData}>
            Refresh
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            Export
          </Button>
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            Print
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        {data && (
          <>
            {/* Summary Statistics */}
            <Row gutter={24} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Statistic
                  title="Total Assets"
                  value={data.assets.total}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Total Liabilities"
                  value={data.liabilities.total}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Total Equity"
                  value={data.equity.total}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Retained Earnings"
                  value={data.equity.retainedEarnings}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: data.equity.retainedEarnings >= 0 ? '#52c41a' : '#f5222d' }}
                />
              </Col>
            </Row>

            <Divider />

            {/* ASSETS SECTION */}
            <Title level={5} style={{ color: '#1890ff' }}>ASSETS</Title>

            {data.assets.current.length > 0 && (
              <>
                {renderSectionTable(
                  'Current Assets',
                  data.assets.current,
                  data.assets.totalCurrent,
                  'Total Current Assets',
                  '#e6f7ff'
                )}
                <div style={{ marginBottom: 16 }} />
              </>
            )}

            {data.assets.nonCurrent.length > 0 && (
              <>
                {renderSectionTable(
                  'Non-Current Assets',
                  data.assets.nonCurrent,
                  data.assets.totalNonCurrent,
                  'Total Non-Current Assets',
                  '#e6f7ff'
                )}
                <div style={{ marginBottom: 16 }} />
              </>
            )}

            {/* Total Assets */}
            <Row justify="end">
              <Col span={8}>
                <Card size="small" style={{ backgroundColor: '#e6f7ff' }}>
                  <Row justify="space-between" align="middle">
                    <Text strong>TOTAL ASSETS</Text>
                    <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                      {formatCurrency(data.assets.total)}
                    </Title>
                  </Row>
                </Card>
              </Col>
            </Row>

            <Divider />

            {/* LIABILITIES SECTION */}
            <Title level={5} style={{ color: '#fa8c16' }}>LIABILITIES</Title>

            {data.liabilities.current.length > 0 && (
              <>
                {renderSectionTable(
                  'Current Liabilities',
                  data.liabilities.current,
                  data.liabilities.totalCurrent,
                  'Total Current Liabilities',
                  '#fff7e6'
                )}
                <div style={{ marginBottom: 16 }} />
              </>
            )}

            {data.liabilities.nonCurrent.length > 0 && (
              <>
                {renderSectionTable(
                  'Non-Current Liabilities',
                  data.liabilities.nonCurrent,
                  data.liabilities.totalNonCurrent,
                  'Total Non-Current Liabilities',
                  '#fff7e6'
                )}
                <div style={{ marginBottom: 16 }} />
              </>
            )}

            {/* Total Liabilities */}
            <Row justify="end">
              <Col span={8}>
                <Card size="small" style={{ backgroundColor: '#fff7e6' }}>
                  <Row justify="space-between" align="middle">
                    <Text strong>TOTAL LIABILITIES</Text>
                    <Title level={4} style={{ margin: 0, color: '#fa8c16' }}>
                      {formatCurrency(data.liabilities.total)}
                    </Title>
                  </Row>
                </Card>
              </Col>
            </Row>

            <Divider />

            {/* EQUITY SECTION */}
            <Title level={5} style={{ color: '#722ed1' }}>EQUITY</Title>

            {data.equity.items.length > 0 && (
              <Table
                columns={columns}
                dataSource={data.equity.items}
                rowKey="accountId"
                pagination={false}
                size="small"
              />
            )}

            {/* Retained Earnings Row */}
            <Table
              columns={columns}
              dataSource={[{
                accountId: 'retained-earnings',
                accountCode: '-',
                accountName: 'Retained Earnings',
                balance: data.equity.retainedEarnings,
                percentOfTotal: showPercentages && data.assets.total > 0
                  ? (data.equity.retainedEarnings / data.assets.total) * 100
                  : undefined
              }]}
              rowKey="accountId"
              pagination={false}
              size="small"
              showHeader={false}
              style={{ marginTop: 8 }}
            />

            {/* Total Equity */}
            <Row justify="end" style={{ marginTop: 16 }}>
              <Col span={8}>
                <Card size="small" style={{ backgroundColor: '#f9f0ff' }}>
                  <Row justify="space-between" align="middle">
                    <Text strong>TOTAL EQUITY</Text>
                    <Title level={4} style={{ margin: 0, color: '#722ed1' }}>
                      {formatCurrency(data.equity.total)}
                    </Title>
                  </Row>
                </Card>
              </Col>
            </Row>

            <Divider />

            {/* Total Liabilities & Equity */}
            <Row justify="end">
              <Col span={8}>
                <Card
                  size="small"
                  style={{
                    backgroundColor: data.balanced ? '#f6ffed' : '#fff2f0',
                    border: `2px solid ${data.balanced ? '#52c41a' : '#f5222d'}`
                  }}
                >
                  <Row justify="space-between" align="middle">
                    <Text strong>TOTAL LIABILITIES & EQUITY</Text>
                    <Title level={4} style={{ margin: 0, color: data.balanced ? '#52c41a' : '#f5222d' }}>
                      {formatCurrency(data.totalLiabilitiesAndEquity)}
                    </Title>
                  </Row>
                </Card>
              </Col>
            </Row>

            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <Text type="secondary">
                As of: {dayjs(data.asOfDate).format('MMMM DD, YYYY')}
              </Text>
              {data.previousDate && (
                <Text type="secondary" style={{ marginLeft: 16 }}>
                  | Compared to: {dayjs(data.previousDate).format('MMMM DD, YYYY')}
                </Text>
              )}
            </div>
          </>
        )}
      </Spin>
    </Card>
  );
};

export default BalanceSheetReport;